import { useEffect, useRef } from 'react'

import { F0Box, F0Button, F0Icon, F0Text } from '@factorialco/f0-react'
import { Tooltip } from '@factorialco/f0-react/dist/experimental'
import { Ai, Feed } from '@factorialco/f0-react/icons/app'

import { useAiChat } from 'modules/ai/chat/useAiChat'
import i18n from 'modules/core/lib/i18n'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import { computeMarketPosition } from 'modules/job_catalog/benchmark/lib/marketPosition'

import { BenchmarkMatchResult } from 'modules/job_catalog/benchmark/types'

/**
 * Approximate months since a survey year. The benchmark API only exposes the
 * survey `year` (no month/day), so this assumes the data was collected at the
 * start of that year. The result is therefore a rough estimate — e.g. a 2025
 * survey queried in January 2026 would show "12 months ago".
 *
 * Returns `null` when the survey year is current (0 months elapsed) so the
 * caller can omit the "last updated" label for recent data.
 */
const getMonthsSinceYear = (year: number | null | undefined): number | null => {
  if (year == null) return null
  const now = new Date()
  const months = (now.getFullYear() - year) * 12 + now.getMonth()
  if (months <= 0) return null
  return months
}
interface SalaryDisplay {
  amount: string
  periodicity: string
}

type BodyStatus = 'hasData' | 'noInfo' | 'suppressed'

interface Props {
  status: BodyStatus
  benchmark: BenchmarkMatchResult
  levelName: string
  roleName?: string
  familyName?: string
  functionName?: string
  workplaceName?: string
  salaryDisplay: SalaryDisplay
  mappingType?: 'manual' | 'auto' | null
  from: string
}

interface BuildPromptParams {
  levelName: string
  roleName?: string
  familyName?: string
  functionName?: string
  workplaceName?: string
}

export function buildBenchmarkReportPrompt({
  levelName,
  roleName,
  familyName,
  functionName,
  workplaceName,
}: BuildPromptParams): string {
  const path = [familyName, functionName, roleName].filter(Boolean).join(' > ')
  const location = workplaceName ?? ''
  if (path && location) {
    return i18n.t('job_catalog.benchmark.one_report_prompt', {
      path,
      levelName,
      workplaceName: location,
    })
  } else if (path) {
    return i18n.t('job_catalog.benchmark.one_report_prompt_no_workplace', { path, levelName })
  } else if (location) {
    return i18n.t('job_catalog.benchmark.one_report_prompt_no_path', {
      levelName,
      workplaceName: location,
    })
  }
  return i18n.t('job_catalog.benchmark.one_report_prompt_minimal', { levelName })
}

const MarketSalaryForRoleInWorkplace = ({
  roleName,
  levelName,
  workplaceName,
}: {
  roleName: string
  levelName: string
  workplaceName: string
}) => {
  const roleLevelName = [roleName, levelName].join(' ')

  return (
    <F0Text
      variant="description"
      content={i18n.t('job_catalog.benchmark.market_salary_for_role_combined_in_workplace', {
        roleLevelName,
        roleName,
        levelName,
        workplaceName,
      })}
    />
  )
}

const MarketSalaryForRole = ({ roleName, levelName }: { roleName: string; levelName: string }) => {
  const roleLevelName = [roleName, levelName].join(' ')

  return (
    <F0Text
      variant="description"
      content={i18n.t('job_catalog.benchmark.market_salary_for_role_combined', {
        roleLevelName,
        roleName,
        levelName,
      })}
    />
  )
}

const MarketSalaryForLevel = ({ levelName }: { levelName: string }) => {
  return (
    <F0Text
      variant="description"
      content={i18n.t('job_catalog.benchmark.market_salary_for_level', { levelName })}
    />
  )
}

const getBenchmarkMetadata = (benchmark: BenchmarkMatchResult): string => {
  const metadata = [
    benchmark.obsCount != null && benchmark.orgCount != null
      ? i18n.tp('job_catalog.benchmark.benchmark_based_on_data', {
          count: benchmark.orgCount,
          surveysLabel: i18n.tp('job_catalog.benchmark.surveys_count', {
            count: benchmark.obsCount,
          }),
          companiesCount: benchmark.orgCount,
        })
      : null,
    (() => {
      const months = getMonthsSinceYear(benchmark.year)
      if (months == null) return null
      return i18n.tp('job_catalog.benchmark.last_updated', { count: months })
    })(),
  ]

  return metadata.filter(Boolean).join(' ')
}

const SalarySummaryLine = ({
  roleName,
  levelName,
  workplaceName,
}: {
  roleName?: string
  levelName: string
  workplaceName?: string
}) => {
  if (roleName && workplaceName) {
    return (
      <MarketSalaryForRoleInWorkplace
        roleName={roleName}
        levelName={levelName}
        workplaceName={workplaceName}
      />
    )
  }

  if (roleName) {
    return <MarketSalaryForRole roleName={roleName} levelName={levelName} />
  }

  return <MarketSalaryForLevel levelName={levelName} />
}

const ViewReportButton = ({ disabled, onClick }: { disabled: boolean; onClick: () => void }) => {
  return (
    <F0Box borderRadius="lg" overflow="hidden">
      <F0Button
        label={i18n.t('job_catalog.benchmark.view_report')}
        variant="neutral"
        size="md"
        disabled={disabled}
        onClick={onClick}
      />
    </F0Box>
  )
}

const SalaryPopoverBody = ({
  status,
  benchmark,
  levelName,
  roleName,
  familyName,
  functionName,
  workplaceName,
  salaryDisplay,
  mappingType,
  from,
}: Props) => {
  const aiChat = useAiChat()
  const sendTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (sendTimerRef.current) clearTimeout(sendTimerRef.current)
    }
  }, [])

  const handleViewReport = () => {
    if (!aiChat.enabled) return

    track(trackingEvents.jobCatalog.viewBenchmarkReportSubmit, {
      event_category: 'Organizations',
      from,
      benchmark_status: benchmark.status,
      market_position: status === 'hasData' ? computeMarketPosition(benchmark) : 'unknown',
    })

    const prompt = buildBenchmarkReportPrompt({
      levelName,
      roleName,
      familyName,
      functionName,
      workplaceName,
    })
    if (sendTimerRef.current) clearTimeout(sendTimerRef.current)
    aiChat.clear()
    aiChat.setOpen(true)
    sendTimerRef.current = setTimeout(() => {
      aiChat.sendMessage(prompt)
    }, 150)
  }

  if (status === 'noInfo' || status === 'suppressed') {
    const description =
      status === 'noInfo'
        ? i18n.t('job_catalog.benchmark.states.no_data.description')
        : i18n.t('job_catalog.benchmark.states.suppressed.description')

    return (
      // eslint-disable-next-line factorial/no-raw-html -- exact grouped layout for empty benchmark state not achievable via F0Box
      <div
        style={{
          boxSizing: 'border-box',
          width: 392,
          height: 156,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          background: 'white',
          padding: '8px 0 12px',
          borderRadius: 12,
          position: 'relative',
        }}
      >
        {/* eslint-disable-next-line factorial/no-raw-html -- exact dimensions and padding for empty state content block not achievable via F0Box */}
        <div
          style={{
            boxSizing: 'border-box',
            width: 392,
            height: 84,
            display: 'flex',
            flexDirection: 'row',
            gap: 8,
            padding: '12px 16px 12px 12px',
            borderRadius: 12,
          }}
        >
          {/* eslint-disable-next-line factorial/no-raw-html -- wrapper div for icon sizing */}
          <div>
            <Feed color="hsl(var(--neutral-solid-40))" width={32} height={32} />
          </div>
          {/* eslint-disable-next-line factorial/no-raw-html -- wrapper div for text */}
          <div>
            <F0Text variant="body" content={description} />
          </div>
        </div>

        {/* eslint-disable-next-line factorial/no-raw-html -- exact empty state footer layout not achievable via F0Box */}
        <div
          style={{
            boxSizing: 'border-box',
            width: 392,
            height: 56,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 8,
            padding: '0 16px',
          }}
        >
          <ViewReportButton disabled={!aiChat.enabled} onClick={handleViewReport} />
        </div>
      </div>
    )
  }

  const benchmarkMetadata = getBenchmarkMetadata(benchmark)

  return (
    // eslint-disable-next-line factorial/no-raw-html -- exact border, padding, and box-sizing not achievable via F0Box tokens
    <div
      style={{
        boxSizing: 'border-box',
        width: 392,
        minHeight: 156,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        gap: 8,
        padding: '8px 16px 12px',
        borderRadius: 12,
        background: 'white',
        position: 'relative',
      }}
    >
      {mappingType === 'auto' && (
        /* eslint-disable-next-line factorial/no-raw-html -- Absolute positioning anchor for tooltip not available via F0Box */
        <div
          role="img"
          aria-label={String(i18n.t('job_catalog.benchmark.auto_mapped.aria_label'))}
          tabIndex={0}
          className="group cursor-help rounded-full p-1 text-f1-icon-secondary transition-colors duration-150 hover:text-f1-icon-primary focus-visible:text-f1-icon-primary focus-visible:outline-none"
          style={{ position: 'absolute', top: 12, right: 12, zIndex: 1 }}
        >
          <Tooltip
            delay={350}
            label={String(i18n.t('job_catalog.benchmark.auto_mapped.title'))}
            description={String(i18n.t('job_catalog.benchmark.auto_mapped.description'))}
          >
            <F0Icon icon={Ai} size="md" color="currentColor" />
          </Tooltip>
        </div>
      )}
      {/* eslint-disable-next-line factorial/no-raw-html -- exact dimensions and spacing for salary summary block not achievable via F0Box */}
      <div
        style={{
          boxSizing: 'border-box',
          width: 360,
          minHeight: 96,
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          paddingTop: 8,
          paddingBottom: 8,
          borderRadius: 12,
        }}
      >
        {/* eslint-disable-next-line factorial/no-raw-html -- exact grouping for label and salary row not achievable via F0Box */}
        <div style={{ width: 360, height: 56, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <F0Text
            variant="description"
            content={i18n.t('job_catalog.benchmark.market_salary_range')}
          />

          {/* eslint-disable-next-line factorial/no-raw-html -- baseline alignment for amount + periodicity not achievable via F0Box */}
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'baseline', gap: 4 }}>
            {/* eslint-disable-next-line factorial/no-raw-html -- h3 with custom font-size/weight/letter-spacing not available via F0Text */}
            <h3
              style={{
                margin: 0,
                fontFamily: 'Inter',
                fontWeight: 500,
                fontSize: 22,
                lineHeight: '125%',
                letterSpacing: '0',
                color: 'hsl(var(--neutral-100))',
              }}
            >
              {salaryDisplay.amount}
            </h3>
            {/* eslint-disable-next-line factorial/no-raw-html -- span with custom font styling not available via F0Text */}
            <span
              style={{
                color: 'hsl(var(--neutral-100))',
                fontFamily: 'Inter',
                fontWeight: 400,
                fontSize: 14,
                lineHeight: '145%',
                letterSpacing: '0',
              }}
            >
              {salaryDisplay.periodicity}
            </span>
          </div>
        </div>

        <SalarySummaryLine
          roleName={roleName}
          levelName={levelName}
          workplaceName={workplaceName}
        />
      </div>

      {/* eslint-disable-next-line factorial/no-raw-html -- space-between row for metadata + button not achievable via F0Box */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          width: 360,
        }}
      >
        {benchmarkMetadata && (
          // eslint-disable-next-line factorial/no-raw-html -- flex: 1 + minWidth: 0 for text truncation not achievable via F0Box
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* eslint-disable-next-line factorial/no-raw-html -- tertiary metadata color isn't exposed through F0Text props */}
            <div className="text-f1-foreground-tertiary">
              <F0Text variant="small" content={benchmarkMetadata} />
            </div>
          </div>
        )}
        {/* eslint-disable-next-line factorial/no-raw-html -- right alignment for footer action not achievable via F0Box here */}
        <div style={{ marginLeft: 'auto', flexShrink: 0 }}>
          <ViewReportButton disabled={!aiChat.enabled} onClick={handleViewReport} />
        </div>
      </div>
    </div>
  )
}

export default SalaryPopoverBody
